export type ScrollToElementOptions = {
    /** Default: 'smooth' for UX; tours should pass 'auto'. */
    behavior?: ScrollBehavior;
    /** Where the target should sit in the free viewport. Default 'center'. */
    block?: 'start' | 'center' | 'end';
    /** Extra top inset (sticky header). Default: --shop-header-height. */
    offsetTop?: number;
    /** Extra bottom inset (bottom nav). Default: mobile nav offset under lg. */
    offsetBottom?: number;
};

function readCssLength(varName: string, fallbackPx: number): number {
    if (typeof window === 'undefined') return fallbackPx;
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;height:var(${varName}, ${fallbackPx}px)`;
    document.body.appendChild(probe);
    const h = probe.getBoundingClientRect().height || fallbackPx;
    probe.remove();
    return h;
}

function defaultOffsetTop(): number {
    return readCssLength('--shop-header-height', 112);
}

function defaultOffsetBottom(): number {
    if (typeof window === 'undefined') return 0;
    if (window.matchMedia('(min-width: 1024px)').matches) return 0;
    // Prefer composed offset when present; else height + safe-area probe
    const composed = readCssLength('--mobile-bottom-nav-offset', 0);
    if (composed > 0) return composed;
    const nav = readCssLength('--mobile-bottom-nav-height', 64);
    const probe = document.createElement('div');
    probe.style.cssText =
        'position:absolute;visibility:hidden;padding-bottom:env(safe-area-inset-bottom,0px)';
    document.body.appendChild(probe);
    const safe = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    probe.remove();
    return nav + safe;
}

/**
 * Scroll so `el` is visible in the free viewport (between sticky header and mobile bottom nav).
 * Prefer this over raw scrollIntoView when bottom nav / sticky chrome would hide content.
 */
export function scrollToElement(el: HTMLElement, options: ScrollToElementOptions = {}): void {
    const behavior = options.behavior ?? 'smooth';
    const block = options.block ?? 'center';
    const offsetTop = options.offsetTop ?? defaultOffsetTop();
    const offsetBottom = options.offsetBottom ?? defaultOffsetBottom();

    const rect = el.getBoundingClientRect();
    const freeTop = offsetTop;
    const freeBottom = window.innerHeight - offsetBottom;
    const freeHeight = Math.max(0, freeBottom - freeTop);
    const elHeight = rect.height;

    let targetTopInFree: number;
    if (block === 'start') {
        targetTopInFree = freeTop + 8;
    } else if (block === 'end') {
        targetTopInFree = freeBottom - elHeight - 8;
    } else {
        targetTopInFree = freeTop + (freeHeight - elHeight) / 2;
    }

    const delta = rect.top - targetTopInFree;
    const nextY = Math.max(0, window.scrollY + delta);

    try {
        window.scrollTo({ top: nextY, behavior });
    } catch {
        window.scrollTo(0, nextY);
    }
}
